import "./_modal.scss";

import React, {SyntheticEvent, useEffect, useRef} from "react";
import ReactModal from "react-modal";
import classNames from "classnames";

import {SECOND_IN_MS} from "../../core/util/time/timeConstants";
import {checkTouchPositionToSwipe} from "./util/Modal.utils";

type ModalSize = "regular";

export type ModalProps = Omit<
  ReactModal.Props,
  "contentLabel" | "onRequestClose" | "parentSelector" | "className"
> & {
  contentLabel: string;
  onClose: ReactModal.Props["onRequestClose"] &
    ((event: React.TouchEvent<Element>) => void);
  customClassName?: string;
  children?: React.ReactNode;
  size?: ModalSize;
  shouldPreventScrollOnOverlay?: boolean;
  shouldCloseOnSwipeDown?: boolean;
};

// The required distance between touchStart and touchEnd to be detected as a closing swipe
const MODAL_SWIPE_TO_CLOSE_THRESHOLD = 250;
const MODAL_CLOSE_TIMEOUT =
  parseFloat(
    getComputedStyle(document.documentElement)
      .getPropertyValue("--modal-transition-duration")
      .trim() || "0.2"
  ) * SECOND_IN_MS;

ReactModal.defaultStyles = {};
ReactModal.setAppElement("#root");

function getParent() {
  return document.querySelector("#modal-root") as HTMLElement;
}

function Modal({
  customClassName,
  children,
  closeTimeoutMS = MODAL_CLOSE_TIMEOUT,
  shouldCloseOnOverlayClick = false,
  shouldCloseOnEsc = false,
  bodyOpenClassName,
  isOpen,
  shouldPreventScrollOnOverlay = true,
  onClose,
  size = "regular",
  shouldCloseOnSwipeDown = true,
  ...otherProps
}: ModalProps) {
  const touchPositionRef = useRef({start: 0, end: 0});
  const modalRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (shouldPreventScrollOnOverlay && isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      if (shouldPreventScrollOnOverlay) {
        document.body.style.overflow = "unset";
      }
    };
  }, [isOpen, shouldPreventScrollOnOverlay]);

  return (
    // eslint-disable-next-line
    <div
      style={{position: "absolute"}}
      onClick={handleModalWrapperClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}>
      <ReactModal
        contentRef={(node) => (modalRef.current = node)}
        parentSelector={getParent}
        className={classNames(
          "modal",
          "has-default-border-radius",
          customClassName,
          `modal--${size}`
        )}
        onRequestClose={handleRequestClose}
        closeTimeoutMS={closeTimeoutMS}
        bodyOpenClassName={classNames("ReactModal__Body--open", bodyOpenClassName)}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        shouldCloseOnEsc={shouldCloseOnEsc}
        isOpen={isOpen}
        {...otherProps}>
        {children}
      </ReactModal>
    </div>
  );

  function handleTouchStart(event: React.TouchEvent<Element>) {
    touchPositionRef.current = {start: event.targetTouches[0].clientY, end: 0};
  }

  function handleTouchMove(event: React.TouchEvent<Element>) {
    const newTouchEnd = event.targetTouches[0].clientY;
    const isValidSwipePosition = checkTouchPositionToSwipe({
      modalContainer: modalRef.current!,
      touchStartPosition: touchPositionRef.current.start
    });

    touchPositionRef.current.end = newTouchEnd;
    // Move the modal to the swipe position
    modalRef.current!.style.transform = isValidSwipePosition
      ? `translate(0, calc(-100% - -${newTouchEnd - touchPositionRef.current.start}px))`
      : `translate(0, -100%)`;
  }

  function handleTouchEnd(event: React.TouchEvent<Element>) {
    const distance = touchPositionRef.current.end - touchPositionRef.current.start;
    const isDownSwipe = distance > MODAL_SWIPE_TO_CLOSE_THRESHOLD;
    const isValidSwipePosition = checkTouchPositionToSwipe({
      modalContainer: modalRef.current!,
      touchStartPosition: touchPositionRef.current.start
    });

    if (isDownSwipe && shouldCloseOnSwipeDown && isValidSwipePosition) {
      onClose(event);
    } else {
      // If the swipe distance is lower than the `MODAL_SWIPE_TO_CLOSE_THRESHOLD`, revert the position to the initial
      modalRef.current!.style.transform = `translate(0, -100%)`;
    }
  }

  function handleRequestClose(
    event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>
  ) {
    if (onClose && (shouldCloseOnOverlayClick || shouldCloseOnEsc)) {
      onClose(event);
    }
  }
}

function handleModalWrapperClick(event: SyntheticEvent) {
  // This click handler is added to stop any click events from bubbling outside of the modal
  event.stopPropagation();
}
export default Modal;
